import React, { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import moment from "moment";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Truck, Train, Plane, Ship } from "lucide-react";
import {
  eWayBillMastersAction,
  eWayBillByIrnAction,
} from "../../../redux/actions/sales-action";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import LoadingSpinner from "../../helper-components/loading-spinner";
import InputField from "../../forms/input-field";
import { parseIrpError } from "../e-invoice/create-e-invoice";
import {
  EInvoiceDetails,
  EInvoiceNotFound,
  useEInvoiceRow,
} from "../e-invoice/view-e-invoice";

/* ─── icon map for transport modes (keyed by name from API) ─────────────────── */
const MODE_ICONS = { Road: Truck, Rail: Train, Air: Plane, Ship: Ship };

/* Used when the e-way masters API doesn't return the lists */
const DEFAULT_TRANS_MODES = [
  { name: "Road", value: "1" },
  { name: "Rail", value: "2" },
  { name: "Air", value: "3" },
  { name: "Ship", value: "4" },
];
const DEFAULT_VEHICLE_TYPES = [
  { name: "Regular", value: "R" },
  { name: "Over Dimensional Cargo", value: "O" },
];

const ROAD = "1";
const NON_ROAD = ["2", "3", "4"]; // Rail, Air, Ship

/* ─── shared layout constants (matches create e-way bill) ───────────────────── */
const LW = "10rem";
const LABEL_CLASS =
  "whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]";
const PARENT_CLASS = "flex items-center m-0 p-0 w-full";

/* "2026-10-05" → "05/10/2026" */
const toSlashDate = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("/")) return dateStr;
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const validationSchema = Yup.object({
  distance: Yup.number()
    .typeError("Distance must be a number")
    .required("Distance is required")
    .min(0, "Distance cannot be negative")
    .max(4000, "Distance must be ≤ 4000 km"),

  /* vehicleType — mandatory when transMode is Road */
  vehicle_type: Yup.string().when("mode_of_transport", {
    is: ROAD,
    then: (s) => s.required("Vehicle Type is required for Road transport"),
    otherwise: (s) => s.notRequired(),
  }),

  /* Transporter ID, Vehicle No and Transporter Doc No — always mandatory */
  transporter_id: Yup.string().trim().required("Transporter ID is required"),
  vehicle_no: Yup.string().trim().required("Vehicle No is required"),
  transporter_doc_no: Yup.string().trim().required("Transporter Doc No is required"),

  /* transDocDate — mandatory when transMode is Rail / Air / Ship */
  transporter_doc_date: Yup.string().when("mode_of_transport", {
    is: (mode) => NON_ROAD.includes(mode),
    then: (s) => s.required("Transporter Doc Date is required for Rail / Air / Ship"),
    otherwise: (s) => s.notRequired(),
  }),
});

/* ═══════════════════════════════════════════════════════════════════════════ */
const GenerateEWayBillByIrn = ({ invoiceId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bodyRef = useRef(null);

  const { row, loading, notFound } = useEInvoiceRow(invoiceId);
  const [mastersLoading, setMastersLoading] = useState(true);
  const [transModes, setTransModes] = useState(DEFAULT_TRANS_MODES);
  const [vehicleTypes, setVehicleTypes] = useState(DEFAULT_VEHICLE_TYPES);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    dispatch(
      eWayBillMastersAction(
        (res) => {
          const m = res?.data ?? res ?? {};
          if (m.transMode?.length) setTransModes(m.transMode);
          if (m.vehicleType?.length) setVehicleTypes(m.vehicleType);
          setMastersLoading(false);
        },
        () => setMastersLoading(false)
      )
    );
  }, []);

  if (loading || mastersLoading) return <LoadingSpinner />;
  if (notFound) return <EInvoiceNotFound />;

  const status = row?.eInvoiceStatus ?? {};
  const irn = status?.irn ?? row?.irnDetails?.irn ?? null;
  const canGenerate = status?.status === "GENERATED" && Boolean(irn);

  const initialValues = {
    transporter_name: "",
    transporter_id: "",
    distance: "",
    mode_of_transport: String(transModes[0]?.value ?? ROAD),
    /* Regular by default; ODC only when chosen */
    vehicle_type: String(
      vehicleTypes.find((vt) => String(vt?.value) === "R")?.value ?? vehicleTypes[0]?.value ?? "R"
    ),
    vehicle_no: "",
    transporter_doc_no: "",
    /* today by default; user can change it */
    transporter_doc_date: moment().format("YYYY-MM-DD"),
  };

  const handleSubmit = (values, { setSubmitting }) => {
    const isRoad = values.mode_of_transport === ROAD;
    const payload = {
      soit_id: invoiceId,
      Irn: irn,
      Distance: Number(values.distance) || 0,
      TransMode: values.mode_of_transport,
      ...(values.transporter_id ? { TransId: values.transporter_id.trim().toUpperCase() } : {}),
      ...(values.transporter_name ? { TransName: values.transporter_name.trim() } : {}),
      ...(values.transporter_doc_date ? { TransDocDt: toSlashDate(values.transporter_doc_date) } : {}),
      ...(values.transporter_doc_no ? { TransDocNo: values.transporter_doc_no.trim() } : {}),
      ...(values.vehicle_no
        ? { VehNo: values.vehicle_no.replace(/\s+/g, "").toUpperCase() }
        : {}),
      ...(isRoad && values.vehicle_type ? { VehType: values.vehicle_type } : {}),
    };

    setSubmitError(null);
    dispatch(
      eWayBillByIrnAction(
        payload,
        (res) => {
          setSubmitting(false);
          successToast(res?.message ?? "e-Way Bill generated successfully");
          navigate("/e-way-bills");
        },
        (err) => {
          setSubmitting(false);
          const parsed = parseIrpError(err);
          setSubmitError(parsed);
          errorToast(parsed.details[0] ?? parsed.message ?? "Failed to generate e-Way Bill");
          bodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        }
      )
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting, submitCount, errors }) => (
        <Form className="h-full flex flex-col">
          <div ref={bodyRef} className="flex-1 overflow-y-auto px-6 py-5">

            {/* ── ERRORS ─────────────────────────────────────────────────────── */}
            {!canGenerate && (
              <div className="mb-4 rounded-[8px] border border-[#FECDCA] bg-[#FEF3F2] px-4 py-3 text-[#B42318]" style={{ fontSize: "13px" }}>
                An e-Way Bill can only be generated for an invoice with a generated IRN.
              </div>
            )}
            {submitError && (
              <div className="mb-4 rounded-[8px] border border-[#FECDCA] bg-[#FEF3F2] px-4 py-3 text-[#B42318]" style={{ fontSize: "13px" }}>
                <div className="font-[600] mb-1">{submitError.message}</div>
                {submitError.details.length > 0 && (
                  <ul className="list-disc pl-5">
                    {submitError.details.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                )}
              </div>
            )}
            {submitCount > 0 && Object.keys(errors).length > 0 && (
              <div className="mb-4 rounded-[8px] border border-[#FECDCA] bg-[#FEF3F2] px-4 py-3 text-[#B42318]" style={{ fontSize: "13px" }}>
                <div className="font-[600] mb-1">Please fix the transportation details</div>
                <ul className="list-disc pl-5">
                  {Object.values(errors).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            {/* ── TRANSPORTATION DETAILS ─────────────────────────────────────── */}
            <section className="border border-[var(--light-border-color)] rounded-[10px] overflow-hidden mb-4">
              <div className="px-5 py-3 bg-[var(--create-table-header-bg)] text-white">
                <span className="font-[600] uppercase tracking-wide" style={{ fontSize: "12px" }}>
                  Transportation Details
                </span>
              </div>
              <div className="px-5 py-5 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-5">
                {/* LEFT: Transporter */}
                <div className="flex flex-col gap-5">
                  <InputField
                    name="transporter_name"
                    label="Transporter Name"
                    placeholder="Enter transporter's name"
                    labelWidth={LW}
                    containerClass=""
                    parentClass={PARENT_CLASS}
                    labelClass={LABEL_CLASS}
                    errMsgContainerClass="hidden"
                    allowSpecial={true}
                  />
                  <InputField
                    name="transporter_id"
                    label="Transporter ID"
                    isRequired={true}
                    placeholder="e.g. 29AAACG0569P1Z3"
                    labelWidth={LW}
                    containerClass=""
                    parentClass={PARENT_CLASS}
                    labelClass={LABEL_CLASS}
                    allowSpecial={true}
                  />
                  <div className="flex flex-col gap-1">
                    <InputField
                      name="distance"
                      label="Distance (in Km)"
                      placeholder="Enter distance"
                      type="number"
                      isRequired={true}
                      labelWidth={LW}
                      containerClass=""
                      parentClass={PARENT_CLASS}
                      labelClass={LABEL_CLASS}
                      allowSpecial={false}
                    />
                    <p className="text-[var(--placeholder-color)]" style={{ fontSize: "11px", lineHeight: "1.5", paddingLeft: LW }}>
                      If you enter 0, e-Way Bill system will automatically calculate it based on the dispatch and delivery locations.
                    </p>
                  </div>
                </div>

                {/* RIGHT: Part B */}
                <div className="flex flex-col gap-5">
                  <div className="flex items-center">
                    <label className={`${LABEL_CLASS} flex-shrink-0`} style={{ minWidth: LW }}>
                      Mode of Transportation
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {transModes.map((mode) => {
                        const Icon = MODE_ICONS[mode?.name] ?? Truck;
                        const value = String(mode?.value);
                        const active = values.mode_of_transport === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setFieldValue("mode_of_transport", value)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-[8px] border transition-all font-[500] ${active
                              ? "border-[var(--primary)] text-[var(--primary)] bg-[#e6f1ff]"
                              : "border-[var(--input-border)] text-[var(--nav-heading-color)] bg-[var(--input-bg)] hover:border-[var(--primary)]"
                              }`}
                            style={{ fontSize: "13px" }}
                          >
                            <Icon size={14} />
                            {mode?.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {values.mode_of_transport === ROAD && (
                    <>
                      <div className="flex items-center">
                        <label className={`${LABEL_CLASS} flex-shrink-0`} style={{ minWidth: LW }}>
                          Vehicle Type
                        </label>
                        <div className="flex items-center gap-6">
                          {vehicleTypes.map((vt) => (
                            <label
                              key={vt?.value}
                              className="flex items-center gap-2 cursor-pointer text-[var(--input-color)] text-[length:var(--input-font-size)] font-[var(--input-font-weight)]"
                            >
                              <input
                                type="radio"
                                name="vehicle_type"
                                value={vt?.value}
                                checked={values.vehicle_type === String(vt?.value)}
                                onChange={() => setFieldValue("vehicle_type", String(vt?.value))}
                                className="accent-[var(--primary)] w-4 h-4"
                              />
                              {vt?.name}
                            </label>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  <InputField
                    name="vehicle_no"
                    label="Vehicle No"
                    isRequired={true}
                    placeholder="e.g. GJ05AA0000"
                    labelWidth={LW}
                    containerClass=""
                    parentClass={PARENT_CLASS}
                    labelClass={LABEL_CLASS}
                    allowSpecial={true}
                  />
                  <InputField
                    name="transporter_doc_no"
                    label="Transporter's Doc No"
                    isRequired={true}
                    placeholder="Document number"
                    labelWidth={LW}
                    containerClass=""
                    parentClass={PARENT_CLASS}
                    labelClass={LABEL_CLASS}
                    allowSpecial={true}
                  />
                  <InputField
                    name="transporter_doc_date"
                    label="Transporter's Doc Date"
                    isRequired={NON_ROAD.includes(values.mode_of_transport)}
                    placeholder="dd/MM/yyyy"
                    type="date"
                    labelWidth={LW}
                    containerClass=""
                    parentClass={PARENT_CLASS}
                    labelClass={LABEL_CLASS}
                    allowSpecial={true}
                  />
                </div>
              </div>
            </section>

            {/* ── e-INVOICE DATA (read-only, used for Part A) ─────────────────── */}
            <EInvoiceDetails row={row} />
          </div>

          {/* ════ STICKY FOOTER ═══════════════════════════════════════════════ */}
          <div
            className="border-t border-[var(--create-table-footer-border)] bg-white h-[4rem] flex justify-end items-center px-5 gap-3"
            style={{ boxShadow: "0 -2px 8px rgba(0,0,0,0.04)" }}
          >
            <button
              type="button"
              onClick={() => navigate("/e-invoice")}
              className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
                bg-[var(--draft-button-bg)] cursor-pointer
                text-[var(--draft-button-fg)]
                border border-[var(--draft-button-border)]
                text-[length:var(--draft-button-fs)]
                font-[var(--draft-button-fw)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !canGenerate}
              className="flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
                bg-[var(--primary-button-bg-color)] cursor-pointer
                text-[var(--primary-button-fg-color)]
                border border-[var(--primary-button-bg-color)]
                hover:bg-[var(--primary-button-hover-color)]
                text-[length:var(--primary-button-create-fs)]
                font-[var(--primary-button-font-weight)]
                disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Generating..." : "Generate e-Way Bill"}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default GenerateEWayBillByIrn;
